import { useMemo, useState } from "react";
import { consumptionReportCsv, type ConsumptionReport } from "@/lib/ops/consumptionReport";

function money(n: number): string {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function OpsQbConsumptionSection(props: { report: ConsumptionReport | null }) {
  const { report } = props;
  const [copied, setCopied] = useState(false);
  const csv = useMemo(() => (report ? consumptionReportCsv(report) : ""), [report]);

  async function copyCsv() {
    if (!csv) return;
    try {
      await navigator.clipboard.writeText(csv);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be blocked; table is still copyable */
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="text-sm font-medium">Materials consumption (finished serials)</div>
          <p className="text-xs text-gray-600 dark:text-zinc-400">
            Serials completed in this period and the materials dollars issued to them. Copy this into
            QuickBooks as the audit link; the why stays in the app.
          </p>
        </div>
        <button type="button" className="rounded border px-3 py-1.5 text-sm" disabled={!csv} onClick={() => void copyCsv()}>
          {copied ? "Copied" : "Copy consumption CSV"}
        </button>
      </div>

      {report ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-800 dark:bg-emerald-950/40">
          <div className="text-xs uppercase tracking-wide text-emerald-900 dark:text-emerald-100">
            Materials consumed on finished serials
          </div>
          <div className="text-2xl font-semibold text-emerald-950 dark:text-emerald-50">
            {money(report.total_usd)}
          </div>
          <div className="text-xs text-emerald-900 dark:text-emerald-100">
            {report.serial_count} serial{report.serial_count === 1 ? "" : "s"} completed {report.period_start} →{" "}
            {report.period_end}
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Serial</th>
              <th className="px-3 py-2">Completed</th>
              <th className="px-3 py-2 text-right">Materials</th>
              <th className="px-3 py-2">Why</th>
            </tr>
          </thead>
          <tbody>
            {(report?.rows ?? []).map((row) => (
              <tr key={row.unit_id} className="border-t">
                <td className="px-3 py-1.5 font-medium">{row.serial}</td>
                <td className="px-3 py-1.5">{row.completed_on}</td>
                <td className="px-3 py-1.5 text-right">{money(row.materials_usd)}</td>
                <td className="px-3 py-1.5 text-xs text-gray-600 dark:text-zinc-400">{row.why}</td>
              </tr>
            ))}
            {!report || report.rows.length === 0 ? (
              <tr>
                <td className="px-3 py-6 text-gray-500" colSpan={4}>
                  No serials marked complete in this period. Finish a unit on the build pipeline to see
                  materials dollars and why they left inventory.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
